import Image from 'next/image';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Modal } from '../layouts/Modal';

export interface InducePopupConfig {
  imageUrl: string;
  redirectUrl: string;
}

// デフォルト設定（設定が渡されない場合のフォールバック）
const DEFAULT_POPUP_CONFIG: InducePopupConfig = {
  imageUrl:
    'https://firebasestorage.googleapis.com/v0/b/luminous-prod.appspot.com/o/SubScriptionMenus%2Fsilver_trial_exit_popup.png?alt=media&token=e72eb116-953e-4099-90a0-5dec182bf6c0',
  redirectUrl:
    'https://raysee.jp/subscriptions/XKKVefXTZ2mU8NQZ3Q0J?utm_source=ot&utm_medium=ot&utm_campaign=counsel_kmn_0225',
};

interface Context {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const InducePopContext = createContext<Context>({
  open: false,
  setOpen: () => {
    throw new Error('setOpen() not implemented');
  },
});

interface Props {
  children: ReactNode;
  popupConfig?: InducePopupConfig;
}

export const InducePopProvider: FC<Props> = ({ children, popupConfig = DEFAULT_POPUP_CONFIG }) => {
  const [open, setOpen] = useState(false);
  const timeRef = useRef<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [time, setTime] = useState(0);

  useEffect(() => {
    if (!open) return;

    const currentTime = new Date();
    const inducePopTime = localStorage.getItem('inducePopTime');
    if (!inducePopTime) localStorage.setItem('inducePopTime', currentTime.toString());
    timeRef.current = inducePopTime ? new Date(inducePopTime) : currentTime;

    intervalRef.current = setInterval(() => {
      const currentTime = new Date();
      const diff = currentTime.getTime() - timeRef.current!.getTime();
      setTime(Math.max(0, 1 * 60 * 1000 - diff));
    }, 10);

    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [open]);

  useEffect(() => {
    if (time <= 0 && intervalRef.current) clearInterval(intervalRef.current);
  }, [time]);

  const minutes = Math.floor(time / 60000);
  const seconds = Math.floor((time % 60000) / 1000);
  const milliseconds = Math.floor((time % 1000) / 10);

  return (
    <InducePopContext.Provider value={{ open, setOpen }}>
      {children}
      <Modal isOpen={open} onClose={() => setOpen(false)}>
        <div className="relative">
          <Link href={popupConfig.redirectUrl} disabled={time <= 0}>
            <Image src={popupConfig.imageUrl} alt={''} width={312} height={432} />
          </Link>
          <div className="absolute top-[1.6%] left-[59%]">
            <p className="text-lg font-medium text-white">{minutes}</p>
          </div>
          <div className="absolute top-[1.6%] left-[70.5%]">
            <p className="text-lg font-medium text-white text-right w-8">{seconds}</p>
          </div>
          <div className="absolute top-[1.6%] left-[86.5%]">
            <p className="text-lg font-medium text-white text-right w-8">
              {milliseconds < 10 ? `0${milliseconds}` : milliseconds}
            </p>
          </div>
        </div>
      </Modal>
    </InducePopContext.Provider>
  );
};

const Link: FC<{ href: string; disabled: boolean; children: ReactNode }> = ({
  href,
  disabled,
  children,
}) =>
  disabled ? (
    <div className="hover:cursor-not-allowed">{children}</div>
  ) : (
    <a href={href}>{children}</a>
  );

export const useInducePop = () => useContext(InducePopContext);
